import { useEffect, useMemo, useRef, useState } from "react";
import { X, Upload, Plus, Trash2, Check, Layers, Tag, Users } from "lucide-react";
import { CATALOG, CATEGORIES, BASE_CATEGORIES, LOOK_IDS } from "./catalog.js";
import { SETS } from "./sets.js";
import { RARITIES, RARITY_ORDER } from "./economy.js";
import { IMG_W, IMG_H, ISO_W, ISO_H, CUBE_H } from "./tileset.js";
import { measureBBox } from "./overrides.js";
import {
  customData, saveCustomItem, deleteCustomItem,
  saveCategory, deleteCategory, saveConfrerie, deleteConfrerie,
} from "./customItems.js";
import Sprite from "./Sprite.jsx";
import { t } from "@/i18n";

// ─────────────────────────────────────────────────────────────
// ATELIER DE L'ADMIN — ajouter du contenu sans toucher au code
//
// Trois onglets :
//   Objets     un nouveau bâtiment ou un nouveau sol : image, nom,
//              prix, emprise, catégorie, confrérie éventuelle.
//   Catégories créer / renommer / supprimer les rubriques qui
//              structurent la boutique et l'éditeur.
//   Confréries un nouveau lot : nom, couleur, habitant. Ses bâtiments
//              sont les objets qu'on lui rattache dans l'onglet Objets.
//
// Un objet créé ici est une entrée de catalogue ordinaire dont l'image
// est enregistrée comme un « override » : rendu, vignettes, boutique,
// sac, coffres et éditeur le traitent exactement comme une pièce du
// pack d'origine. Aucun cas particulier ailleurs dans le code.
// ─────────────────────────────────────────────────────────────

const ANCHOR_X = IMG_W / 2 - 0.5;
const DEFAULT_ANCHOR = 182.5 + CUBE_H;

const slug = (s) =>
  (s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
    .slice(0, 40);

const Field = ({ label, children, hint }) => (
  <label className="block space-y-1">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);

const input = "w-full rounded-lg border bg-background px-3 py-1.5 text-sm";

export default function AdminCreate({ onClose, onChanged }) {
  const [tab, setTab] = useState("objets");
  const data = customData();

  const refresh = () => onChanged?.();

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-3xl h-[92vh] sm:h-[86vh] bg-card rounded-t-3xl sm:rounded-3xl border overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Plus size={18} className="text-primary" />
            <span className="font-head font-bold">{t("admincreate.atelier_ajouter_du_contenu")}</span>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-1 p-2 border-b shrink-0">
          {[["objets", "Objets", Layers], ["categories", "Catégories", Tag], ["confreries", "Confréries", Users]].map(
            ([id, label, Icon]) => (
              <button key={id} type="button" onClick={() => setTab(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                  tab === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                }`}>
                <Icon size={13} /> {label}
              </button>
            )
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "objets" && <ItemMaker data={data} onChanged={refresh} />}
          {tab === "categories" && <CategoryMaker data={data} onChanged={refresh} />}
          {tab === "confreries" && <ConfrerieMaker data={data} onChanged={refresh} />}
        </div>
      </div>
    </div>
  );
}

// ── Objets ────────────────────────────────────────────────────
function ItemMaker({ data, onChanged }) {
  const fileRef = useRef(null);
  const canvasRef = useRef(null);

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("batiment");
  const [rarity, setRarity] = useState("commun");
  const [layer, setLayer] = useState("objet");
  const [price, setPrice] = useState(60);
  const [w, setW] = useState(1);
  const [d, setD] = useState(1);
  const [setId, setSetId] = useState("");
  const [anchor, setAnchor] = useState(DEFAULT_ANCHOR);
  const [scale, setScale] = useState(1);
  const [isCube, setIsCube] = useState(true);
  const [url, setUrl] = useState(null);
  const [bbox, setBbox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const footprint = Math.max(1, w, d);
  useEffect(() => { if (!editingId) setScale(footprint); }, [footprint, editingId]);
  // Un sol se pose à plat : il remplace toujours le bloc de terre.
  useEffect(() => { if (layer === "sol") setIsCube(true); }, [layer]);

  const reset = () => {
    setEditingId(null); setName(""); setDesc(""); setPrice(60);
    setW(1); setD(1); setSetId(""); setUrl(null); setBbox(null);
    setAnchor(DEFAULT_ANCHOR); setScale(1); setIsCube(true); setMsg(null);
  };

  const normalize = (file) => {
    const img = new Image();
    img.onload = async () => {
      const c = document.createElement("canvas");
      c.width = IMG_W; c.height = IMG_H;
      const ctx = c.getContext("2d");
      const s = Math.min((IMG_W - 20) / img.width, (IMG_H - 30) / img.height);
      const iw = img.width * s, ih = img.height * s;
      ctx.drawImage(img, (IMG_W - iw) / 2, IMG_H - ih - 10, iw, ih);
      const out = c.toDataURL("image/png");
      setUrl(out);
      setBbox(await measureBBox(out));
    };
    img.src = URL.createObjectURL(file);
  };

  // Aperçu : même calcul que le moteur de rendu.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const W = c.width, H = c.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#2a4a63"; ctx.fillRect(0, 0, W, H);
    const S = 1.05 / Math.max(1, footprint * 0.72);
    const cx = W / 2, cy = H - 36;
    ctx.save();
    ctx.globalAlpha = 0.5; ctx.strokeStyle = "#ffffff88"; ctx.lineWidth = 1.5;
    for (let i = 0; i < w; i++)
      for (let j = 0; j < d; j++) {
        const gx = i - (w - 1) / 2, gy = j - (d - 1) / 2;
        const x = cx + (gx - gy) * (ISO_W / 2) * S;
        const y = cy + (gx + gy) * (ISO_H / 2) * S;
        ctx.beginPath();
        ctx.moveTo(x, y - (ISO_H / 2) * S); ctx.lineTo(x + (ISO_W / 2) * S, y);
        ctx.lineTo(x, y + (ISO_H / 2) * S); ctx.lineTo(x - (ISO_W / 2) * S, y);
        ctx.closePath(); ctx.stroke();
      }
    ctx.restore();
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      const m = S * scale;
      ctx.drawImage(img, cx - ANCHOR_X * m, cy - anchor * m, IMG_W * m, IMG_H * m);
      ctx.strokeStyle = "#ff2d55"; ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(cx, cy - (ISO_H / 2) * S); ctx.lineTo(cx + (ISO_W / 2) * S, cy);
      ctx.lineTo(cx, cy + (ISO_H / 2) * S); ctx.lineTo(cx - (ISO_W / 2) * S, cy);
      ctx.closePath(); ctx.stroke();
    };
    img.src = url;
  }, [url, anchor, scale, w, d, footprint]);

  const edit = (it) => {
    setEditingId(it.id);
    setName(it.name); setDesc(it.desc || "");
    setCat(it.cat || "batiment"); setRarity(it.rarity || "commun");
    setLayer(it.layer || "objet"); setPrice(it.price ?? 60);
    setW(it.size?.[0] || 1); setD(it.size?.[1] || 1);
    setSetId(it.set || ""); setUrl(it.imageDataUrl || null);
    setBbox(it.bbox || null); setAnchor(it.anchorY ?? DEFAULT_ANCHOR);
    setScale(it.scale || 1); setIsCube(!!it.isCube);
    setMsg(null);
  };

  const submit = async () => {
    const id = editingId || slug(name);
    if (!id) return setMsg({ ok: false, text: "Donne-lui un nom." });
    if (!url) return setMsg({ ok: false, text: "Il faut une image." });
    setBusy(true); setMsg(null);
    try {
      await saveCustomItem({
        id, name: name || id, desc,
        cat, rarity, layer,
        price: Math.max(0, Math.round(price) || 0),
        size: [w, d],
        set: setId || null,
        imageDataUrl: url,
        anchorY: anchor, scale, isCube,
        bbox: bbox || (await measureBBox(url)),
      });
      onChanged?.();
      setMsg({ ok: true, text: editingId ? "Objet mis à jour." : "Objet ajouté au catalogue." });
      if (!editingId) reset();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Échec de l'enregistrement." });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet objet du catalogue ? Les exemplaires déjà posés disparaîtront des îles.")) return;
    await deleteCustomItem(id);
    if (editingId === id) reset();
    onChanged?.();
  };

  return (
    <div className="p-4 space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t("lettercard.nom")}>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admincreate.fontaine_du_quartier")} />
        </Field>
        <Field label={t("admincreate.identifiant")} hint={editingId ? "figé après création" : "généré depuis le nom"}>
          <input className={`${input} opacity-60`} readOnly value={editingId || slug(name)} />
        </Field>
      </div>

      <Field label={t("admincreate.description")}>
        <input className={input} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("admincreate.une_phrase_pas_plus")} />
      </Field>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label={t("admincreate.type")}>
          <select className={input} value={layer} onChange={(e) => setLayer(e.target.value)}>
            <option value="objet">{t("admincreate.batiment_decor")}</option>
            <option value="sol">{t("admincreate.sol_chemin")}</option>
          </select>
        </Field>
        <Field label={t("practicesentences.categorie")}>
          <select className={input} value={cat} onChange={(e) => setCat(e.target.value)}>
            {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field label={t("admincreate.rarete")}>
          <select className={input} value={rarity} onChange={(e) => setRarity(e.target.value)}>
            {RARITY_ORDER.map((r) => (
              <option key={r} value={r}>{RARITIES[r].label}</option>
            ))}
          </select>
        </Field>
        <Field label={t("admincreate.prix_dinars")}>
          <input type="number" min={0} className={input} value={price}
            onChange={(e) => setPrice(+e.target.value)} />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label={t("admincreate.largeur_cases")}>
          <input type="number" min={1} max={6} className={input} value={w} onChange={(e) => setW(Math.max(1, Math.min(6, +e.target.value || 1)))} />
        </Field>
        <Field label={t("admincreate.profondeur_cases")}>
          <input type="number" min={1} max={6} className={input} value={d} onChange={(e) => setD(Math.max(1, Math.min(6, +e.target.value || 1)))} />
        </Field>
        <Field label={t("admincreate.confrerie")} hint="3 bâtiments rattachés = 1 habitant">
          <select className={input} value={setId} onChange={(e) => setSetId(e.target.value)}>
            <option value="">{t("admincreate.aucune")}</option>
            {SETS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      </div>

      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f?.type.startsWith("image/")) normalize(f); }}
        className="rounded-2xl border-2 border-dashed border-muted-foreground/30 p-4 text-center cursor-pointer hover:bg-muted transition"
      >
        <Upload size={20} className="mx-auto mb-1.5 text-muted-foreground" />
        <p className="text-sm font-medium">{t("admincreate.glisse_l_image_de_l_objet")}</p>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) normalize(f); }} />
      </div>

      <canvas ref={canvasRef} width={340} height={240} className="w-full rounded-2xl border bg-muted" />

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="flex justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            <span>{t("admincreate.hauteur_du_calage")}</span><span className="tabular-nums normal-case">{Math.round(anchor)}</span>
          </div>
          <input type="range" min={80} max={420} step={1} value={anchor}
            onChange={(e) => setAnchor(+e.target.value)} className="w-full accent-primary" />
        </div>
        <div>
          <div className="flex justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            <span>{t("admincreate.taille")}</span><span className="tabular-nums normal-case">×{scale.toFixed(2)}</span>
          </div>
          <input type="range" min={0.5} max={6} step={0.05} value={scale}
            onChange={(e) => setScale(+e.target.value)} className="w-full accent-primary" />
        </div>
      </div>

      <label className="flex items-center gap-2.5 rounded-xl border p-3 cursor-pointer">
        <input type="checkbox" checked={isCube} disabled={layer === "sol"}
          onChange={(e) => setIsCube(e.target.checked)} className="accent-primary" />
        <div>
          <div className="text-sm font-semibold">{t("admincreate.bloc_plein")}</div>
          <div className="text-[11px] text-muted-foreground">
            {t("admincreate.coche_remplace_le_sol_decoche_se")}
            {layer === "sol" && " Un sol est toujours un bloc plein."}
          </div>
        </div>
      </label>

      {msg && (
        <p className={`text-xs flex items-center gap-1.5 ${msg.ok ? "text-emerald-600" : "text-destructive"}`}>
          {msg.ok && <Check size={13} />} {msg.text}
        </p>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={submit} disabled={busy}
          className="flex-1 rounded-2xl bg-primary text-primary-foreground py-2.5 text-sm font-bold disabled:opacity-40 hover:brightness-110 transition">
          {editingId ? "Mettre à jour" : "Ajouter au catalogue"}
        </button>
        {editingId && (
          <button type="button" onClick={reset}
            className="rounded-2xl border px-4 py-2.5 text-sm font-semibold hover:bg-muted transition">
            {t("admincreate.nouveau")}
          </button>
        )}
      </div>

      <div className="pt-2 border-t">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {t("admincreate.objets_crees")}{data.items.length})
        </div>
        {data.items.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("rewards.rien_pour_l_instant")}</p>
        ) : (
          <div className="space-y-1">
            {data.items.map((it) => (
              <div key={it.id} className="flex items-center gap-2 rounded-lg bg-muted px-2 py-1.5">
                <Sprite item={CATALOG.find((c) => c.id === it.id) || { kind: "tile", id: it.id, name: it.name, size: it.size }} size={28} />
                <span className="flex-1 min-w-0 truncate text-xs">
                  {it.name}
                  <span className="text-muted-foreground"> · {it.price} 🪙 · {it.size?.[0]}×{it.size?.[1]}</span>
                </span>
                <button type="button" onClick={() => edit(it)}
                  className="text-[11px] rounded px-2 py-1 bg-background hover:bg-card">{t("admincreate.modifier")}</button>
                <button type="button" onClick={() => remove(it.id)}
                  className="rounded p-1 text-red-600 hover:bg-red-500/10"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Catégories ────────────────────────────────────────────────
function CategoryMaker({ data, onChanged }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const baseIds = useMemo(() => new Set(BASE_CATEGORIES.map((c) => c.id)), []);

  const add = async () => {
    const id = slug(label);
    if (!id) return;
    setBusy(true);
    try {
      await saveCategory({ id, label: label.trim() });
      onChanged?.();
      setLabel("");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4">
      <p className="text-xs text-muted-foreground">
        {t("admincreate.les_categories_organisent_la_boutique_et")}
      </p>
      <div className="flex gap-2">
        <input className={input} value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder={t("admincreate.nom_de_la_categorie_ex_fontaines")} />
        <button type="button" onClick={add} disabled={busy || !label.trim()}
          className="shrink-0 rounded-lg bg-primary text-primary-foreground px-4 text-sm font-bold disabled:opacity-40">
          {t("admincreate.creer")}
        </button>
      </div>
      <div className="space-y-1">
        {CATEGORIES.filter((c) => c.id !== "all").map((c) => {
          const locked = baseIds.has(c.id);
          const count = CATALOG.filter((i) => i.cat === c.id).length;
          return (
            <div key={c.id} className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
              <span className="flex-1 truncate">
                {c.label}
                <span className="text-muted-foreground text-xs"> · {count} objet{count > 1 ? "s" : ""}</span>
              </span>
              {locked ? (
                <span className="text-[10px] text-muted-foreground">{t("admincreate.d_origine")}</span>
              ) : (
                <button type="button" onClick={async () => { await deleteCategory(c.id); onChanged?.(); }}
                  className="rounded p-1 text-red-600 hover:bg-red-500/10"><Trash2 size={13} /></button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Confréries ────────────────────────────────────────────────
function ConfrerieMaker({ data, onChanged }) {
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [color, setColor] = useState("#c98a4b");
  const [npcName, setNpcName] = useState("");
  const [npcDesc, setNpcDesc] = useState("");
  const [look, setLook] = useState(LOOK_IDS[0]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const add = async () => {
    const id = slug(name);
    if (!id || !npcName.trim()) return setMsg({ ok: false, text: "Nom de la confrérie et de l'habitant requis." });
    setBusy(true); setMsg(null);
    try {
      await saveConfrerie({
        id, name: name.trim(), tagline, color,
        npc: { id: `${id}_${slug(npcName)}`, name: npcName.trim(), desc: npcDesc, look },
      });
      onChanged?.();
      setName(""); setTagline(""); setNpcName(""); setNpcDesc("");
      setMsg({ ok: true, text: "Confrérie créée. Rattache-lui des objets dans l'onglet Objets." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Échec." });
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4">
      <p className="text-xs text-muted-foreground">
        {t("admincreate.une_confrerie_c_est_un_lot")}
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t("admincreate.nom_de_la_confrerie")}>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admincreate.les_potiers")} />
        </Field>
        <Field label={t("admincreate.couleur")}>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
            className="w-full h-9 rounded-lg border bg-background" />
        </Field>
      </div>
      <Field label={t("admincreate.accroche")}>
        <input className={input} value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder={t("admincreate.la_terre_tourne_la_journee_passe")} />
      </Field>

      <div className="grid sm:grid-cols-3 gap-3">
        <Field label={t("admincreate.nom_de_l_habitant")}>
          <input className={input} value={npcName} onChange={(e) => setNpcName(e.target.value)} placeholder={t("admincreate.potiere")} />
        </Field>
        <Field label={t("admincreate.description")}>
          <input className={input} value={npcDesc} onChange={(e) => setNpcDesc(e.target.value)} />
        </Field>
        <Field label={t("admincreate.apparence")}>
          <select className={input} value={look} onChange={(e) => setLook(e.target.value)}>
            {LOOK_IDS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
      </div>

      {msg && (
        <p className={`text-xs flex items-center gap-1.5 ${msg.ok ? "text-emerald-600" : "text-destructive"}`}>
          {msg.ok && <Check size={13} />} {msg.text}
        </p>
      )}

      <button type="button" onClick={add} disabled={busy}
        className="w-full rounded-2xl bg-primary text-primary-foreground py-2.5 text-sm font-bold disabled:opacity-40">
        {t("admincreate.creer_la_confrerie")}
      </button>

      <div className="pt-2 border-t space-y-1">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          {t("admincreate.confreries_creees")}{data.confreries.length})
        </div>
        {data.confreries.length === 0 && <p className="text-xs text-muted-foreground">{t("rewards.rien_pour_l_instant")}</p>}
        {data.confreries.map((s) => {
          const live = SETS.find((x) => x.id === s.id);
          return (
            <div key={s.id} className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
              <span className="flex-1 truncate">
                {s.name}
                <span className="text-muted-foreground text-xs"> · {live?.buildings.length || 0} {t("admincreate.batiment_s")} {s.npc?.name}</span>
              </span>
              <button type="button"
                onClick={async () => {
                  if (!window.confirm("Supprimer cette confrérie ?")) return;
                  await deleteConfrerie(s.id); onChanged?.();
                }}
                className="rounded p-1 text-red-600 hover:bg-red-500/10"><Trash2 size={13} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
